import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";

// Components
import PremiumSidebar from "@/components/PremiumSidebar";

// Pages
import LoginPage from "@/pages/PremiumLoginPage";
import NewDashboardPage from "@/pages/NewDashboardPage";
import TrialPage from "@/pages/TrialPage";
import UsersPage from "@/pages/UsersPage";
import IntegrationPage from "@/pages/IntegrationPage";
import ProductFoldersPage from "@/pages/ProductFoldersPage";
import ProductFolderDetailPage from "@/pages/ProductFolderDetailPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import ReportsPage from "@/pages/ReportsPage";
import ClientRequirementFormPage from "@/pages/ClientRequirementFormPage";
import ClientRequirementsPage from "@/pages/ClientRequirementsPage";
import ClientRequirementDetailPage from "@/pages/ClientRequirementDetailPage";
import SampleDispatchesPage from "@/pages/SampleDispatchesPage";
import SyncQueuePage from "@/pages/SyncQueuePage";
import ProductDevelopmentReportPage from "@/pages/ProductDevelopmentReportPage";
import ProductDevelopmentReportDetailPage from "@/pages/ProductDevelopmentReportDetailPage";
import SampleFeedbackReportPage from "@/pages/SampleFeedbackReportPage";
import SampleDevelopmentReportPage from "@/pages/SampleDevelopmentReportPage";
import ClientRequirementDashboardPage from "@/pages/ClientRequirementDashboardPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      if (response.data.success) {
        localStorage.setItem("user", JSON.stringify(response.data.user));
        setUser(response.data.user);
        return { success: true };
      }
      return { success: false, error: "Login failed" };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || "Login failed" };
    }
  };

  const logout = () => {
    localStorage.removeItem("user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-vcc-cream-50 via-white to-vcc-pink-50">
        <div className="animate-pulse text-vcc-teal font-poppins">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AppLayout = ({ children }) => {
  return (
    <div className="flex min-h-screen bg-gradient-to-br from-vcc-cream-50 via-white to-vcc-pink-50">
      <PremiumSidebar />
      <main className="flex-1 ml-72 overflow-auto">
        {children}
      </main>
    </div>
  );
};

// Public layout without sidebar for guest users
const PublicLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-vcc-cream-50 via-white to-vcc-pink-50">
      {children}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="bottom-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Public Route - Client Requirement Form accessible without login */}
          <Route path="/client-requirement" element={<PublicLayout><ClientRequirementFormPage /></PublicLayout>} />
          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRoute><AppLayout><NewDashboardPage /></AppLayout></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute><AppLayout><ProductFoldersPage /></AppLayout></ProtectedRoute>} />
          <Route path="/products/:folderId" element={<ProtectedRoute><AppLayout><ProductFolderDetailPage /></AppLayout></ProtectedRoute>} />
          <Route path="/product-development-report" element={<ProtectedRoute><AppLayout><ProductDevelopmentReportPage /></AppLayout></ProtectedRoute>} />
          <Route path="/product-development-report/:folderId" element={<ProtectedRoute><AppLayout><ProductDevelopmentReportDetailPage /></AppLayout></ProtectedRoute>} />
          <Route path="/client-requirement-dashboard" element={<ProtectedRoute><AppLayout><ClientRequirementDashboardPage /></AppLayout></ProtectedRoute>} />
          <Route path="/sample-dispatches" element={<ProtectedRoute><AppLayout><SampleDispatchesPage /></AppLayout></ProtectedRoute>} />
          <Route path="/sample-feedback-report" element={<ProtectedRoute><AppLayout><SampleFeedbackReportPage /></AppLayout></ProtectedRoute>} />
          <Route path="/sample-development-report" element={<ProtectedRoute><AppLayout><SampleDevelopmentReportPage /></AppLayout></ProtectedRoute>} />
          <Route path="/sync-queue" element={<ProtectedRoute><AppLayout><SyncQueuePage /></AppLayout></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><AppLayout><ClientsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/clients/:clientId" element={<ProtectedRoute><AppLayout><ClientDetailPage /></AppLayout></ProtectedRoute>} />
          <Route path="/client-requirements" element={<ProtectedRoute><AppLayout><ClientRequirementsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/client-requirements/:requirementId" element={<ProtectedRoute><AppLayout><ClientRequirementDetailPage /></AppLayout></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><AppLayout><ReportsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><AppLayout><UsersPage /></AppLayout></ProtectedRoute>} />
          <Route path="/integration" element={<ProtectedRoute><AppLayout><IntegrationPage /></AppLayout></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
